import { ThemeEvents } from '@theme/events';

/**
 * Sticky ATC mobile OTWS.
 * Affiché seulement quand le bouton d'ajout principal est hors de l'écran (et masqué quand un autre bouton d'achat,
 * le pied de page ou le panier est visible).
 */
class OtwsStickyAtcMobile extends HTMLElement {
  constructor() {
    super();

    this.onVariantUpdated = this.onVariantUpdated.bind(this);
    this.updateVisibility = this.updateVisibility.bind(this);
    this.queueVisibilityUpdate = this.queueVisibilityUpdate.bind(this);

    this.frame = null;
  }

  connectedCallback() {
    this.variantInput = this.querySelector('input[name="id"]');
    this.addButton = this.querySelector('[data-ref="add-to-cart-button"]');
    this.addButtonContainer = this.querySelector('[data-ref="add-to-cart-button-container"]');
    this.buttonLabel = this.querySelector('[data-sticky-atc-button-label]');
    this.priceTargets = this.querySelectorAll('[data-sticky-atc-price]');
    this.compareAtPriceTargets = this.querySelectorAll('[data-sticky-atc-compare-price]');
    this.productImage = this.querySelector('[data-sticky-atc-image]');

    this.mainProductForm = this.findMainProductForm();

    document.addEventListener(ThemeEvents.variantUpdated, this.onVariantUpdated);

    this.observeVisibility();
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.variantUpdated, this.onVariantUpdated);
    window.removeEventListener('scroll', this.queueVisibilityUpdate);
    window.removeEventListener('resize', this.queueVisibilityUpdate);
    this.drawerObserver?.disconnect();
    if (this.frame) window.cancelAnimationFrame(this.frame);
  }

  /**
   * Formulaire produit de la section principale (hors sticky, hors tiroir panier et hors ajout rapide).
   * @returns {HTMLElement|null}
   */
  findMainProductForm() {
    const candidates = [
      ...document.querySelectorAll('[id$="__main"] product-form'),
      ...document.querySelectorAll('main product-form'),
    ];

    return (
      candidates.find(
        (form) =>
          !this.contains(form) &&
          !form.closest('cart-drawer, quick-add-modal, dialog') &&
          form.querySelector('form[action$="/cart/add"]')
      ) || null
    );
  }

  observeVisibility() {
    if (!this.mainProductForm) {
      this.dataset.active = 'true';
      return;
    }

    this.footer = document.querySelector('[data-ref="footer"]') || document.querySelector('footer');

    window.addEventListener('scroll', this.queueVisibilityUpdate, { passive: true });
    window.addEventListener('resize', this.queueVisibilityUpdate, { passive: true });

    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      this.drawerObserver = new MutationObserver(this.queueVisibilityUpdate);
      this.drawerObserver.observe(cartDrawer, { attributes: true, attributeFilter: ['class'] });
    }

    this.updateVisibility();
  }

  queueVisibilityUpdate() {
    if (this.frame) return;
    this.frame = window.requestAnimationFrame(() => {
      this.frame = null;
      this.updateVisibility();
    });
  }

  /**
   * Le bouton principal est relu à chaque fois : le thème le remplace quand la variante change.
   */
  updateVisibility() {
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const isInViewport = (element) => {
      if (!element) return false;
      const rect = element.getBoundingClientRect();
      return rect.bottom > 0 && rect.top < viewportHeight;
    };

    const mainButton =
      this.mainProductForm?.querySelector('[data-ref="add-to-cart-button"]') ||
      this.mainProductForm?.querySelector('[type="submit"]');
    const mainButtonVisible = isInViewport(mainButton);
    // Autres boutons d'achat de la page (ex. hero de landing) : le sticky reste masqué tant qu'ils sont visibles.
    const otherBuyButtonVisible = [...document.querySelectorAll('[data-sticky-atc-hide]')].some(isInViewport);
    const footerVisible = isInViewport(this.footer);
    const cartDrawerOpen = document.querySelector('cart-drawer.is-open') !== null;

    const active = !mainButtonVisible && !otherBuyButtonVisible && !footerVisible && !cartDrawerOpen;
    this.dataset.active = active ? 'true' : 'false';
    this.toggleAttribute('inert', !active);
  }

  onVariantUpdated(event) {
    const variant = event.detail?.resource;
    if (!variant?.id) return;

    const isAvailable = variant.available !== false;

    if (this.variantInput) {
      this.variantInput.value = variant.id;
    }

    if (this.addButton) {
      this.addButton.disabled = !isAvailable;
    }

    if (this.buttonLabel) {
      this.buttonLabel.textContent = isAvailable ? this.dataset.buttonLabel : this.dataset.soldOutLabel;
    }

    if (this.addButtonContainer) {
      this.addButtonContainer.dataset.price = variant.price;
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        this.addButtonContainer.dataset.compareAtPrice = variant.compare_at_price;
      } else {
        delete this.addButtonContainer.dataset.compareAtPrice;
      }
    }

    this.priceTargets.forEach((priceTarget) => {
      priceTarget.textContent = this.formatMoney(variant.price);
    });

    this.compareAtPriceTargets.forEach((compareAtPriceTarget) => {
      compareAtPriceTarget.textContent =
        variant.compare_at_price && variant.compare_at_price > variant.price
          ? this.formatMoney(variant.compare_at_price)
          : '';
    });

    const imageUrl = variant.featured_media?.preview_image?.src;
    if (this.productImage && imageUrl) {
      this.productImage.removeAttribute('srcset');
      this.productImage.src = imageUrl;
    }
  }

  formatMoney(cents) {
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents, Theme.moneyFormat);
    }

    return new Intl.NumberFormat(document.documentElement.lang || 'fr-FR', {
      style: 'currency',
      currency: Theme.currency || 'EUR',
      minimumFractionDigits: Theme.trailingZeros ? 2 : 0,
      maximumFractionDigits: 2,
    }).format(cents / 100);
  }
}

if (!customElements.get('otws-sticky-atc-mobile')) {
  customElements.define('otws-sticky-atc-mobile', OtwsStickyAtcMobile);
}
